import { useContext, useMemo } from "react";
import { GeaoContext } from "../../../GeaoContextProvider";
import useCobrandedAgency from "../../../hooks/useCobrandedAgency";
import { isCobrandedSubAgency } from "../../../mappers/InterviewMapper";

export default function useMappers(formData) {
    const { agencyInterview, branchInterview1, branchInterview2 } = useContext(GeaoContext);
    const { isCobrandedAgency } = useCobrandedAgency();
    const mapIfExists = (key, value) => value !== null && value !== undefined ? { [key]: { answer: value.toString() } } : {};

    const defaultValues = useMemo(() => {
        const agencyAnswer = questionType => agencyInterview?.questionAnswers?.[questionType]?.answer;
        const branch1Answer = questionType => branchInterview1?.questionAnswers?.[questionType]?.answer;
        const branch2Answer = questionType => branchInterview2?.questionAnswers?.[questionType]?.answer;

        // For cobranded agencies, the backend sets:
        // - AffiliatedWithTop100 = "false"
        // - AgencyCommissionPayments = "false"
        // - ParentAgencyName, ParentAgencyEmailAddress, ParentAgencyFEIN
        // The UI only provides defaults for hidden fields NOT set by backend
        const cobrandedDefaults = isCobrandedAgency ? {
            w9RegistrationAsIndividual: agencyAnswer("W9RegistrationAsIndividual") || "false",
            feinOrSsn: agencyAnswer("W9FeinOrSsn") || "fein",
            centersOfOperationOutsidePrimaryLocation: agencyAnswer("CentersOfOperationOutsidePrimaryLocation") || "false"
        } : {};

        return {
            dba: agencyAnswer("DoingBusinessAs"),
            agencyNpn: agencyAnswer("Npn") || agencyAnswer("SolePropPrincipalNpn"),
            yearsInBusiness: agencyAnswer("YearsInBusiness"),
            w9RegistrationAsIndividual: isCobrandedAgency ? cobrandedDefaults.w9RegistrationAsIndividual : agencyAnswer("W9RegistrationAsIndividual"),
            feinOrSsn: isCobrandedAgency ? cobrandedDefaults.feinOrSsn : agencyAnswer("W9FeinOrSsn"),
            fein: agencyAnswer("Fein"),
            ssn: agencyAnswer("Ssn"),
            entityType: agencyAnswer("EntityType"),
            partnershipType: agencyAnswer("PartnershipType"),
            entityFiledState: agencyAnswer("EntityFiledState"),
            physicalAddressLine1: agencyAnswer("PrimaryPhysicalAddressStreetLine1"),
            physicalAddressLine2: agencyAnswer("PrimaryPhysicalAddressStreetLine2"),
            physicalAddressZipCode: agencyAnswer("PrimaryPhysicalAddressZipCode"),
            physicalAddressCity: agencyAnswer("PrimaryPhysicalAddressCity"),
            physicalAddressState: agencyAnswer("PrimaryPhysicalAddressState"),
            mailingAddressLine1: agencyAnswer("PrimaryMailingAddressStreetLine1"),
            mailingAddressLine2: agencyAnswer("PrimaryMailingAddressStreetLine2"),
            mailingAddressZipCode: agencyAnswer("PrimaryMailingAddressZipCode"),
            mailingAddressCity: agencyAnswer("PrimaryMailingAddressCity"),
            mailingAddressState: agencyAnswer("PrimaryMailingAddressState"),
            // For cobranded agencies, backend sets AffiliatedWithTop100 = "false"
            affiliatedWithTop100: agencyAnswer("AffiliatedWithTop100"),
            firmNetworkOrAggregator: [agencyAnswer("FirmNetworkAggregator")],
            firmNetworkAggregatorCommissionPayments: agencyAnswer("FirmNetworkAggregatorCommissionPayments"),
            agencyGroupName: agencyAnswer("AgencyGroupName"),
            agencyGroupEmailAddress: agencyAnswer("AgencyGroupEmailAddress"),
            agencyGroupFEIN: agencyAnswer("AgencyGroupFEIN"),
            // For cobranded agencies, backend sets AgencyCommissionPayments = "false"
            agencyCommissionPayments: agencyAnswer("AgencyCommissionPayments"),
            // For cobranded agencies, backend sets these from parent agency lookup
            parentAgencyName: agencyAnswer("ParentAgencyName"),
            parentAgencyEmailAddress: agencyAnswer("ParentAgencyEmailAddress"),
            parentAgencyFein: agencyAnswer("ParentAgencyFEIN"),
            centersOfOperationOutsidePrimaryLocation: isCobrandedAgency ? cobrandedDefaults.centersOfOperationOutsidePrimaryLocation : agencyAnswer("CentersOfOperationOutsidePrimaryLocation"),
            numberOfCentersOfOperationUnderSameEin: agencyAnswer("NumberOfCentersOfOperationUnderSameEin"),

            // Branch 1 fields
            branchName: branch1Answer("BranchName"),
            branchAddressLine1: branch1Answer("BranchAddressLine1"),
            branchAddressLine2: branch1Answer("BranchAddressLine2"),
            branchAddressZipCode: branch1Answer("BranchAddressZipCode"),
            branchAddressCity: branch1Answer("BranchAddressCity"),
            branchAddressState: branch1Answer("BranchAddressState"),
            branchContactFirstName: branch1Answer("BranchContactFirstName"),
            branchContactLastName: branch1Answer("BranchContactLastName"),
            branchContactEmail: branch1Answer("BranchContactEmail"),
            branchContactRole: branch1Answer("BranchContactRole"),
            branchPhone: branch1Answer("BranchOfficePhoneNumber"),

            // Branch 2 fields
            branch2Name: branch2Answer("BranchName"),
            branch2AddressLine1: branch2Answer("BranchAddressLine1"),
            branch2AddressLine2: branch2Answer("BranchAddressLine2"),
            branch2AddressZipCode: branch2Answer("BranchAddressZipCode"),
            branch2AddressCity: branch2Answer("BranchAddressCity"),
            branch2AddressState: branch2Answer("BranchAddressState"),
            branch2ContactFirstName: branch2Answer("BranchContactFirstName"),
            branch2ContactLastName: branch2Answer("BranchContactLastName"),
            branch2ContactEmail: branch2Answer("BranchContactEmail"),
            branch2ContactRole: branch2Answer("BranchContactRole"),
            branch2Phone: branch2Answer("BranchOfficePhoneNumber"),

            // Additional fields
            mailingSameAsPrimaryPhysical: agencyAnswer("PrimaryMailingAddressSameAsPrimaryPhysical") === "true",
            physicalSameAsPrincipalResidential: agencyAnswer("PhysicalAddressSameAsResidential") === "true",

            // Cobranded commissions acknowledgement (only relevant for cobranded sub-agencies)
            cobrandedCommissionsAgreement: agencyAnswer("CobrandedCommissionsAgreement") === "true"
        }
    }, [agencyInterview, branchInterview1, branchInterview2, isCobrandedAgency]);

    const agencyInterviewQuestionAnswers = useMemo(() => {
        return formData ? {
            ...mapIfExists("PrimaryPhysicalAddressStreetLine1", formData.physicalAddressLine1),
            ...mapIfExists("PrimaryPhysicalAddressStreetLine2", formData.physicalAddressLine2),
            ...mapIfExists("PrimaryPhysicalAddressZipCode", formData.physicalAddressZipCode),
            ...mapIfExists("PrimaryPhysicalAddressCity", formData.physicalAddressCity),
            ...mapIfExists("PrimaryPhysicalAddressState", formData.physicalAddressState),
            ...mapIfExists("PrimaryPhysicalAddressCountry", "US"),
            ...mapIfExists("PrimaryMailingAddressStreetLine1", formData.mailingAddressLine1),
            ...mapIfExists("PrimaryMailingAddressStreetLine2", formData.mailingAddressLine2),
            ...mapIfExists("PrimaryMailingAddressZipCode", formData.mailingAddressZipCode),
            ...mapIfExists("PrimaryMailingAddressCity", formData.mailingAddressCity),
            ...mapIfExists("PrimaryMailingAddressState", formData.mailingAddressState),
            ...mapIfExists("PrimaryMailingAddressCountry", "US"),
            ...mapIfExists("DoingBusinessAs", formData.dba),
            ...mapIfExists("Npn", formData.agencyNpn),
            ...mapIfExists("SolePropPrincipalNpn", formData.w9RegistrationAsIndividual === "true" ? formData.agencyNpn : null),
            ...mapIfExists("YearsInBusiness", formData.yearsInBusiness),
            ...mapIfExists("W9RegistrationAsIndividual", formData.w9RegistrationAsIndividual),
            ...mapIfExists("W9FeinOrSsn", formData.feinOrSsn),
            ...mapIfExists("Fein", formData.fein),
            ...mapIfExists("Ssn", formData.ssn),
            ...mapIfExists("EntityType", formData.entityType),
            ...mapIfExists("PartnershipType", formData.partnershipType),
            ...mapIfExists("EntityFiledState", formData.entityFiledState),
            ...mapIfExists("AffiliatedWithTop100", formData.affiliatedWithTop100),
            ...mapIfExists("FirmNetworkAggregator", formData.firmNetworkOrAggregator),
            ...mapIfExists("FirmNetworkAggregatorCommissionPayments", formData.firmNetworkAggregatorCommissionPayments),
            ...mapIfExists("AgencyGroupName", formData.agencyGroupName),
            ...mapIfExists("AgencyGroupEmailAddress", formData.agencyGroupEmailAddress),
            ...mapIfExists("AgencyGroupFEIN", formData.agencyGroupFEIN),
            ...mapIfExists("AgencyCommissionPayments", formData.agencyCommissionPayments ?? "false"),
            ...mapIfExists("ParentAgencyName", formData.parentAgencyName),
            ...mapIfExists("ParentAgencyEmailAddress", formData.parentAgencyEmailAddress),
            ...mapIfExists("ParentAgencyFEIN", formData.parentAgencyFein),
            ...mapIfExists("CentersOfOperationOutsidePrimaryLocation", formData.centersOfOperationOutsidePrimaryLocation),
            ...mapIfExists("NumberOfCentersOfOperationUnderSameEin", formData.numberOfCentersOfOperationUnderSameEin),
            ...mapIfExists("PrimaryMailingAddressSameAsPrimaryPhysical", formData.mailingSameAsPrimaryPhysical),
            ...mapIfExists("PhysicalAddressSameAsResidential", formData.physicalSameAsPrincipalResidential),
            // Only include CobrandedCommissionsAgreement when agency is a cobranded sub-agency and checkbox is checked
            ...(isCobrandedSubAgency(agencyInterview) && formData.cobrandedCommissionsAgreement ? { CobrandedCommissionsAgreement: { answer: "true" } } : {})
        } : null;
    }, [formData, agencyInterview]);

    const branch1InterviewQuestionAnswers = useMemo(() => {
        return formData ? {
            IsBranch: { answer: "true" },
            ...mapIfExists("BranchName", formData.branchName),
            ...mapIfExists("BranchAddressLine1", formData.branchAddressLine1),
            ...mapIfExists("BranchAddressLine2", formData.branchAddressLine2),
            ...mapIfExists("BranchAddressZipCode", formData.branchAddressZipCode),
            ...mapIfExists("BranchAddressCity", formData.branchAddressCity),
            ...mapIfExists("BranchAddressState", formData.branchAddressState),
            ...mapIfExists("BranchContactFirstName", formData.branchContactFirstName),
            ...mapIfExists("BranchContactLastName", formData.branchContactLastName),
            ...mapIfExists("BranchContactEmail", formData.branchContactEmail),
            ...mapIfExists("BranchContactRole", formData.branchContactRole),
            ...mapIfExists("BranchOfficePhoneNumber", formData.branchPhone)
        } : null;
    }, [formData]);

    const branch2InterviewQuestionAnswers = useMemo(() => {
        return formData ? {
            IsBranch: { answer: "true" },
            ...mapIfExists("BranchName", formData.branch2Name),
            ...mapIfExists("BranchAddressLine1", formData.branch2AddressLine1),
            ...mapIfExists("BranchAddressLine2", formData.branch2AddressLine2),
            ...mapIfExists("BranchAddressZipCode", formData.branch2AddressZipCode),
            ...mapIfExists("BranchAddressCity", formData.branch2AddressCity),
            ...mapIfExists("BranchAddressState", formData.branch2AddressState),
            ...mapIfExists("BranchContactFirstName", formData.branch2ContactFirstName),
            ...mapIfExists("BranchContactLastName", formData.branch2ContactLastName),
            ...mapIfExists("BranchContactEmail", formData.branch2ContactEmail),
            ...mapIfExists("BranchContactRole", formData.branch2ContactRole),
            ...mapIfExists("BranchOfficePhoneNumber", formData.branch2Phone)
        } : null;
    }, [formData]);

    return {
        agencyInterviewQuestionAnswers,
        branch1InterviewQuestionAnswers,
        branch2InterviewQuestionAnswers,
        defaultValues
    };
}